import { RowNode } from "ag-grid-community";
import { dispatcher, Event } from "../../../../../../common/utils/dispatcher";
import { CostGroup } from "../../../CalculationApi";
import { childless } from "../constants";

const oneHourInMinutes: number = 60;

class Calculator {
  private laborIK: number;
  private articleFactor: number;
  private cacheTotalTime: any;
  private cacheCostUnit: any;
  private costGroups: CostGroup[];

  constructor() {
    this.laborIK = 1;
    this.articleFactor = 1; //1.325;

    this.emptyCache();
    this.costGroups = [];
  }

  /**
   * @private
   * empties the cache;
   * cache has the purpose to short the
   * recursive calls to some of the nodes.
   */
  emptyCache() {
    this.cacheTotalTime = {};
    this.cacheCostUnit = {};
  }

  costGroupExcluded(data: any): boolean {
    return (
      !!data?.cg &&
      (this.costGroups.findIndex((cg: CostGroup) => cg.id === data?.cg?.id) ===
        -1 ||
        this.costGroups.length === 0)
    );
  }

  excludeItemFromTotalCalculation(data: any): boolean {
    return (
      !!data &&
      (!data?.rowType ||
        data?.rowType === "provisionalItem" ||
        data?.rowType === "optional" ||
        this.costGroupExcluded(data))
    );
  }

  /**
   *
   * @param {*} node
   * @private
   */
  calculateTotalTime(node: any) {
    if (this.cacheTotalTime[node.id]) {
      return this.cacheTotalTime[node.id];
    }

    if (!node.childrenAfterGroup.length) {
      let calculatedTime = parseFloat(node.data.totalTime) || 0;
      if (node.data.rowType === "labor") {
        const minPerUnit = this.calculateMinPerUnit(node) || 1;
        calculatedTime =
          this.calculateTf(node) * node.data.quantity * minPerUnit;
      }

      this.cacheTotalTime[node.id] = calculatedTime;
      return calculatedTime;
    }
    const calculatedTime =
      node.childrenAfterGroup.reduce(
        (rx: any, el: any) => parseFloat(this.calculateTotalTime(el) || 0) + rx,
        0
      ) * parseFloat(node.data.quantity);
    this.cacheTotalTime[node.id] = calculatedTime;
    return calculatedTime;
  }

  calculateMinPerUnit(node: RowNode): number | undefined {
    if (!!node.parent) {
      const result = this.calculateMinPerUnit(node?.parent);

      if (!!result) {
        return result;
      }
    }

    return node.data.minPerUnit;
  }

  calculateTf(node: RowNode): number {
    return this.calculateTfRecursive(node) || 1;
  }

  calculateTfRecursive(node: RowNode): number | undefined {
    let result: number | undefined;
    if (!!node?.parent) {
      result = this.calculateTfRecursive(node?.parent);

      if (!!result) {
        return result;
      }
    }

    return !!node.data?.tf ? parseFloat(node.data?.tf) : undefined;
  }

  /**
   *
   * @param {*} node
   * @private
   * util function for calculateTotalPrice
   */
  getSupposedUnitPrice(node: RowNode) {
    // if (node.data.rowType === "article" && !node.data.vp) {
    //   // determine the IK factor
    //   const ikFactor = this.calculateIkFactor(node) || this.articleFactor;
    //   return parseFloat(node.data.costsUnit || 0) * ikFactor;
    // }
    return parseFloat(node.data.unitPrice) || 0;
  }

  /**
   * @private
   * @param {*} node
   */
  calculateTotalPrice(node: RowNode): number {
    let result: number = 0;
    const totalTime = this.calculateTotalTime(node);
    const unitPrice = this.calculateUnitPrice(node);
    if (node.data.rowType === "labor") {
      result = (totalTime / oneHourInMinutes) * unitPrice;
    } else {
      result = parseFloat(node.data.quantity) * unitPrice;
    }

    return result;
  }

  /**
   * @private
   * @param {*} node
   */
  calculateTotalCost(node: RowNode): number {
    const costsUnit = this.calculateCostUnit(node) || 0;
    let result = 0;
    if (node.data.rowType === "labor") {
      const totalTime = this.calculateTotalTime(node);
      const mpcPerUnit = this.calculateMpcPerUnit(node);
      result = (totalTime / oneHourInMinutes) * mpcPerUnit;
    } else {
      result = parseFloat(node.data.quantity) * costsUnit || 0;
    }

    return result;
  }

  /**
   * @private
   * @param {*} node
   */
  calculateVatPercentage(node: RowNode): number | undefined {
    let percentage: number | undefined;
    if (!!node?.parent) {
      percentage = this.calculateVatPercentage(node?.parent);
      if (!!percentage) {
        return percentage;
      }
    }
    return !!node.data?.btw ? parseFloat(node.data?.btw) : undefined;
  }

  calculateRaise(node: RowNode) {
    if (
      node.data.rowType === "provisionalItem" ||
      node.data.rowType === "optional"
    ) {
      return 0;
    }
    const totalPrice = this.calculateTotalPrice(node);
    const totalCost = this.calculateTotalCost(node);
    return (totalPrice / totalCost) * 100 - 100;
  }

  calculateVatAmount(node: RowNode, percentage?: number): number {
    if (!node?.childrenAfterGroup?.length) {
      let btw = this.calculateVatPercentage(node) || 0;
      if (
        node.data.rowType === "provisionalItem" ||
        node.data.rowType === "optional"
      ) {
        btw = parseInt(node.data.btw, 10) || 0;
      }
      if (btw === percentage) {
        const totalPrice = this.calculateTotalPrice(node);
        const vatAmount = (totalPrice / 100) * btw;
        return vatAmount;
      } else {
        return 0;
      }
    }

    const parentVatAmount =
      node.childrenAfterGroup.reduce((rx: any, el: RowNode) => {
        if (this.excludeItemFromTotalCalculation(el.data)) {
          return rx;
        }
        const vatAmount = this.calculateVatAmount(el, percentage) || 0;
        return vatAmount + rx;
      }, 0) * parseFloat(node.data.quantity);
    return parentVatAmount;
  }

  /**
   *
   * @param node
   * @returns
   */
  calculateCostUnit(node: RowNode): number {
    if (!!node.id && !!this.cacheCostUnit[node.id]) {
      return this.cacheCostUnit[node.id];
    }

    let result: number = 0;
    if (node.data.rowType === "labor") {
      const mpcPerUnit = this.calculateMpcPerUnit(node);
      result =
        (mpcPerUnit / oneHourInMinutes) * this.laborIK * this.calculateTf(node);
    } else if (
      node.data.rowType === "article" ||
      node.data.rowType === "additional"
    ) {
      const mpcPerUnit = this.calculateMpcPerUnit(node);
      result =
        mpcPerUnit * this.articleFactor * this.calculateIkFactor(node) || 0;
    } else {
      // calculate total of all children
      if (!node.childrenAfterGroup || node.childrenAfterGroup?.length === 0) {
        result = node.data.costsUnit || 0;
      } else {
        result = node.childrenAfterGroup.reduce((rx: any, el: RowNode) => {
          if (this.excludeItemFromTotalCalculation(el.data)) {
            return rx;
          }

          const totalCost = this.calculateTotalCost(el) || 0;
          return totalCost + rx;
        }, 0);
      }
    }

    this.cacheCostUnit[node.id!] = result;
    return result;
  }

  calculateIkFactor(node: RowNode): number {
    const cg = this.getCalculationGroup(node);
    return !!cg?.ikFactor ? cg?.ikFactor : 1;
  }

  calculateUnitPrice(node: RowNode): number {
    let result: number = 0;
    if (!!node.data.vp) {
      return node.data?.unitPrice;
    }

    const unitPrice = node.data.originalUnitPrice ?? node.data.unitPrice;
    if (node.data.rowType === "labor") {
      result =
        (node.data.hourlyCostGroup?.sellingPrice || 1) *
        this.calculateSurcharge(node);
    } else if (node.data.rowType === "article") {
      result =
        unitPrice * this.articleFactor * this.calculateSurcharge(node) || 0;
    } else {
      // calculate total of all children
      if (!node.childrenAfterGroup || node.childrenAfterGroup?.length === 0) {
        const costUnit = this.calculateCostUnit(node);
        const surcharges = this.calculateSurcharge(node);

        result = costUnit * surcharges || 0;
      } else {
        result = node.childrenAfterGroup.reduce(
          (rx: any, el: any, index: number) => {
            if (this.excludeItemFromTotalCalculation(el.data)) {
              return rx;
            }

            let value = 0;
            if (childless.includes(el.data.rowType)) {
              value = this.calculateTotalPrice(el) || 0;
            } else {
              value = el.data.quantity * this.calculateUnitPrice(el) || 0;
            }

            return value + rx;
          },
          0
        );
      }
    }

    return result;
  }

  calculateSurcharge(node: RowNode): number {
    const cg = this.getCalculationGroup(node);

    let surcharge: number = 0;
    if (node.data.rowType === "labor") {
      surcharge = cg?.surchargeLabor ?? 0;
    } else {
      surcharge = cg?.surchargeArticle ?? 0;
    }

    return !!surcharge ? surcharge / 100 + 1 : 1;
  }

  getCalculationGroup(node?: RowNode): CostGroup | undefined {
    let result: any;

    if (!!node?.data?.cg) {
      return node.data.cg;
    } else {
      if (!!node?.parent) {
        result = this.getCalculationGroup(
          !!node?.parent ? node?.parent : undefined
        );

        if (!!result) {
          return result;
        }
      }
    }

    return undefined;
  }

  calculateMpcPerUnit(node: RowNode): number {
    if (node.data.rowType === "labor") {
      return node.data.hourlyCostGroup?.purchasePrice || 0;
    } else if (
      node.data.rowType === "article" ||
      node.data.rowType === "additional"
    ) {
      return (
        parseFloat(node.data.mpcPerUnit) || parseFloat(node.data.costsUnit)
      );
    }

    return 0;
  }

  flatDataToChildStructure(data: any) {
    return data.reduce((rx: any, item: any) => {
      const newItem = {
        id: item.id,
        data: { ...item },
        childrenAfterGroup: [],
        parent: undefined,
        level:
          !!item.typeHierarchy && item.typeHierarchy?.length > 0
            ? item.typeHierarchy?.length - 1
            : undefined,
      };

      if (
        !!newItem.data.typeHierarchy &&
        newItem.data.typeHierarchy.length > 1
      ) {
        const parentId =
          newItem.data.typeHierarchy[newItem.data.typeHierarchy.length - 2];
        const parent = rx.find((existingItem: any) => {
          return existingItem.id === parentId;
        });

        if (!!parent) {
          parent.childrenAfterGroup.push(newItem);
          newItem.parent = parent;
        }
      }

      rx.push(newItem);
      return rx;
    }, []);
  }

  calculateFlatData(
    data: any,
    costGroups: CostGroup[] = []
  ): { data: any; totals: any } {
    if (!!data) {
      const result = this.flatDataToChildStructure(data);

      return this.calculate(result, costGroups);
    }

    return { data: [], totals: {} };
  }

  /**
   * public
   * @param {*} data array of ag-grid nodes
   */
  calculate(data: any, costGroups: CostGroup[] = []) {
    this.emptyCache();
    this.costGroups = costGroups;
    const dataWithDefaultValues = data.map((el: any) => {
      const newEl = { ...el };
      newEl.data.originalUnitPrice =
        el.data.originalUnitPrice ?? el.data.unitPrice;
      if (!el.data.quantity) {
        newEl.data.quantity = 1;
        return newEl;
      }

      return newEl;
    });

    const calculatedData = dataWithDefaultValues.map((el: any) => {
      dispatcher.dispatchEvent(new Event("CALCULATION_QUANTITY", el));
      const mpcPerUnit = this.calculateMpcPerUnit(el);
      el.data.mpcPerUnit = mpcPerUnit;
      const totalTime = this.calculateTotalTime(el) || 0;
      el.data.totalTime = totalTime;
      const unitPrice = this.calculateUnitPrice(el);
      el.data.unitPrice = unitPrice;
      const costsUnit = this.calculateCostUnit(el);
      el.data.costsUnit = costsUnit;
      const totalCost = this.calculateTotalCost(el) || 0;
      el.data.totalCost = totalCost;

      const totalPrice = this.calculateTotalPrice(el) || 0;
      el.data.totalPrice = totalPrice;

      let vatAmountLow = this.calculateVatAmount(el, 9);
      let vatAmountHigh = this.calculateVatAmount(el, 21);
      let vatAmount = vatAmountLow + vatAmountHigh;

      const raise = this.calculateRaise(el) || 0;

      return {
        ...el,
        data: {
          ...el.data,
          totalTime,
          totalPrice,
          totalCost,
          costsUnit,
          unitPrice,
          raise,
          vatAmount,
          vatAmountLow,
          vatAmountHigh,
          mpcPerUnit,
          // tf,
          // btw,
        },
      };
    });

    const grandTotals = this.calculateTotals(calculatedData);
    return {
      data: calculatedData.map((el: any) => el.data),
      totals: grandTotals,
    };
  }

  calculateTotals(data: any) {
    const allTotals = data.reduce(
      (rx: any, { level, data }: any) => {
        if (this.excludeItemFromTotalCalculation(data) || level !== 0) {
          return rx;
        }
        return {
          sumTotalPrice: rx.sumTotalPrice + (data.totalPrice || 0),
          sumTotalTime: rx.sumTotalTime + (data.totalTime || 0),
          sumCostsTotal: rx.sumCostsTotal + (data.totalCost || 0),
          totalVat: rx.totalVat + (data.vatAmount || 0),
          totalVatLow: rx.totalVatLow + (data.vatAmountLow || 0),
          totalVatHigh: rx.totalVatHigh + (data.vatAmountHigh || 0),
        };
      },
      {
        sumTotalPrice: 0,
        sumTotalTime: 0,
        sumCostsTotal: 0,
        totalVat: 0,
        totalVatLow: 0,
        totalVatHigh: 0,
      }
    );

    const raise =
      (allTotals.sumTotalPrice / allTotals.sumCostsTotal) * 100 - 100;
    const grandTotals = {
      ...allTotals,
      manDays: allTotals.sumTotalTime / 480,
      raise: raise,
    };

    return grandTotals;
  }
}

export default Calculator;
